// The built-in status pictures on tokens (qa-runs#1440; decision qa-runs#1447: game-icons.net, CC BY 3.0). The files
// are the client's own static assets under /status-icons/ (their authors in credits.json beside them; the credit line
// is on the site's Licenses page), shipped with the client like its app icons: no upload, no viewer data, nothing on
// the wire. A condition's picture is chosen here by its name; an effect's by the key the DM picked (`Effect.icon`).
import { Texture } from 'pixi.js';

/** A condition's picture, by its lowercased name (every system's conditions, the wound words and Concentrating). */
const CONDITION_ICONS: Readonly<Record<string, string>> = {
  'ability damaged': 'broken-bone', 'ability drained': 'heart-drop', bleed: 'bleeding-wound', bleeding: 'bleeding-wound',
  blinded: 'blindfold', blind: 'blindfold', 'blown away': 'half-tornado', broken: 'broken-shield', charmed: 'charm',
  checked: 'stop-sign', confused: 'spiral-arrow', cowering: 'kneeling', dazed: 'star-swirl', dazzled: 'striped-sun',
  dead: 'dead-head', deafened: 'sound-off', deaf: 'sound-off', disabled: 'half-body-crawling', dying: 'half-dead',
  'energy drained': 'life-tap', entangled: 'vines', exhausted: 'despair', exhaustion: 'despair',
  fascinated: 'psychic-waves', fatigued: 'tired-eye', 'flat-footed': 'footprint', frightened: 'screaming',
  grappled: 'grab', grappling: 'hand-grip', helpless: 'handcuffed', incapacitated: 'cancel', incorporeal: 'ghost',
  invisible: 'invisible', 'knocked down': 'foot-trip', nauseated: 'vomiting', panicked: 'run', paralyzed: 'frozen-body',
  petrified: 'stone-bust', pinned: 'pin', poisoned: 'poison-bottle', prone: 'falling', restrained: 'manacles',
  shaken: 'terror', sickened: 'stomach', stable: 'life-support', staggered: 'unbalanced', stunned: 'knocked-out-stars',
  turned: 'holy-symbol', unconscious: 'sleepy', concentrating: 'meditation',
};

/** The pictures the DM may give an effect, in the picker's order: the two generic ones first, then the condition set. */
export const EFFECT_ICON_CHOICES: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'magic-swirl', label: 'Spell' }, { key: 'upgrade', label: 'Boost' },
  ...[...new Map(Object.entries(CONDITION_ICONS).map(([name, key]) => [key, name] as const)).entries()]
    .map(([key, name]) => ({ key, label: name.charAt(0).toUpperCase() + name.slice(1) }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];
const KNOWN = new Set(EFFECT_ICON_CHOICES.map((c) => c.key));

/** A condition's picture key, or null (its badge is then its first letter). A level ("Exhaustion 2") is not part of the name. */
export function conditionIcon(name: string): string | null {
  const bare = name.trim().replace(/\s+\d+$/, '').toLowerCase();
  return CONDITION_ICONS[bare] ?? null;
}
/** An effect's picture key when it is one of the set; anything else is no picture. */
export const effectIcon = (key: string | undefined): string | null => (key && KNOWN.has(key) ? key : null);
/** The file a key is drawn from: only a key of the set, so no other path is ever built. */
export const iconUrl = (key: string): string | null => (KNOWN.has(key) ? `/status-icons/${key}.svg` : null);

// Each picture is rasterised once, at a size that stays sharp on a large token, and kept for the session.
const SIZE = 96;
const textures = new Map<string, Texture | 'loading' | 'failed'>();
const listeners = new Set<() => void>();
/** Called whenever a picture finished loading, so the board redraws the badges waiting on it. */
export function onIconLoaded(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }

/** The picture's texture if it is loaded; otherwise starts loading it and returns null (the badge shows its letter until then). */
export function iconTexture(key: string): Texture | null {
  const have = textures.get(key);
  if (have instanceof Texture) return have;
  if (have) return null;
  const url = iconUrl(key);
  if (!url || typeof document === 'undefined') return null;
  textures.set(key, 'loading');
  const img = new Image();
  img.src = url;
  void img.decode().then(() => {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE; canvas.height = SIZE;
    canvas.getContext('2d')?.drawImage(img, 0, 0, SIZE, SIZE);
    textures.set(key, Texture.from(canvas));
    for (const fn of listeners) fn();
  }).catch(() => { textures.set(key, 'failed'); });
  return null;
}
